/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Plus, Shapes, Layers, X } from 'lucide-react';
import { ModeleTable } from './types.ts';

interface BibliothequeModelesProps {
  modeles: ModeleTable[];
  estOuverte: boolean;
  onFermer: () => void;
  onAjouterTableDepuisModele: (modele: ModeleTable) => void;
}

export const BibliothequeModeles: React.FC<BibliothequeModelesProps> = ({
  modeles,
  estOuverte,
  onFermer,
  onAjouterTableDepuisModele,
}) => {
  if (!estOuverte) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="titre-biblio-modeles"
      className="fixed bottom-3 left-3 sm:left-4 z-40 max-w-sm w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-4 select-none animate-in fade-in slide-in-from-bottom-2"
    >
      <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-slate-500" />
          <h3 id="titre-biblio-modeles" className="font-bold text-sm text-slate-900 dark:text-slate-100">
            Formats de l'établissement
          </h3>
        </div>
        <button
          type="button"
          onClick={onFermer}
          aria-label="Fermer la bibliothèque"
          className="p-1 rounded-md text-slate-400 hover:text-slate-600 min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400 my-2">
        Cliquez sur un modèle pour déposer une nouvelle table sur le plan :
      </p>

      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {modeles.map((mod) => (
          <div
            key={mod.id}
            className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 flex items-center justify-between gap-2"
          >
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200">
                  {mod.nomCourt}
                </span>
                <span className="text-xs font-semibold text-slate-900 dark:text-slate-100 capitalize">
                  {mod.forme} • {mod.capaciteMax} pl.
                </span>
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                {mod.largeur} × {mod.hauteur} cm • {mod.places} chaises
                {mod.combinableAvec.length > 0 && (
                  <span className="block text-[10px] text-emerald-600 dark:text-emerald-400">
                    Combinable ({mod.combinableAvec.length} format{mod.combinableAvec.length > 1 ? 's' : ''})
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => onAjouterTableDepuisModele(mod)}
              aria-label={`Ajouter une table modèle ${mod.nomCourt}`}
              className="px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1 cursor-pointer min-h-[44px] shadow-xs active:scale-95"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Ajouter</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
